import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Check, Clock, List, Search } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ColorCell } from "@/components/ColorCell";
import { SystemCell } from "@/components/SystemCell";
import { InlineEditableCell } from "@/components/InlineEditableCell";
import { COLOR_META } from "@/lib/colors";

const noop = () => {};

// Sélecteur rapide d'un élève (liste + recherche) — mobile / tablette.
function StudentPicker({ rows, index, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.map((r, i) => ({ r, i })).filter(({ r }) => !s || r.student_name.toLowerCase().includes(s));
  }, [rows, q]);
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-11 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
        data-testid="student-picker-button"
        aria-label="Liste des élèves"
      >
        <List size={17} /> <span className="tabular-nums">{index + 1} / {rows.length}</span>
      </button>
      <DialogContent className="max-h-[85vh] overflow-hidden rounded-2xl p-0 sm:max-w-[420px]" data-testid="student-picker-dialog">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle>Liste des élèves</DialogTitle>
          <DialogDescription>Accéder directement à un élève.</DialogDescription>
        </DialogHeader>
        <div className="px-4">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 dark:border-white/10">
            <Search size={16} className="text-slate-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un élève…"
              className="h-11 w-full bg-transparent text-base outline-none placeholder:text-slate-300"
              data-testid="student-picker-search"
            />
          </div>
        </div>
        <ul className="max-h-[55vh] overflow-y-auto px-2 pb-3 pt-2" data-testid="student-picker-list">
          {filtered.map(({ r, i }) => (
            <li key={r.passport_id}>
              <button
                type="button"
                onClick={() => { onPick(i); setOpen(false); setQ(""); }}
                className={`flex min-h-[48px] w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 ${
                  i === index ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-white/10"
                }`}
                data-testid={`student-picker-item-${r.student_id}`}
              >
                <span className="w-7 shrink-0 text-right text-xs tabular-nums opacity-60">{String(i + 1).padStart(2, "0")}</span>
                <span className="flex-1 truncate font-medium">{r.student_name}</span>
                {r.signed ? <Check size={16} className="shrink-0 text-emerald-500" aria-label="Signé" /> : <Clock size={15} className="shrink-0 opacity-40" aria-label="En attente" />}
              </button>
            </li>
          ))}
          {filtered.length === 0 && <li className="px-3 py-6 text-center text-sm text-slate-400">Aucun élève trouvé.</li>}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

// Un critère : libellé + libellé du niveau (texte, pas seulement la couleur) + sélecteur tactile.
function CriterionField({ r, c, isCustom, evalSystem, onSelectColor, onSelectLevel, rowIndex, colIndex }) {
  const levelLabel = isCustom
    ? (evalSystem.levels.find((l) => l.id === (r.values || {})[c.key]) || evalSystem.levels.find((l) => l.is_none))?.label
    : (COLOR_META[r[c.key]] || COLOR_META.white).meaning;
  return (
    <div className="rounded-xl border border-slate-100 p-3 dark:border-white/10" data-testid={`card-criterion-${r.student_id}-${c.key}`}>
      <div className="mb-2 flex flex-col gap-0.5">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{c.label}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400" data-testid={`card-level-label-${r.student_id}-${c.key}`}>{levelLabel}</p>
      </div>
      {isCustom ? (
        <SystemCell
          passportId={r.passport_id} criterion={c.key} value={(r.values || {})[c.key]}
          levels={evalSystem.levels} scale={evalSystem.scale_type} studentName={r.student_name}
          rowIndex={rowIndex} colIndex={colIndex} onSelect={onSelectLevel} onNavigate={noop} registerCell={noop} size="lg"
        />
      ) : (
        <ColorCell
          passportId={r.passport_id} criterion={c.key} value={r[c.key]} studentName={r.student_name}
          rowIndex={rowIndex} colIndex={colIndex} onSelect={onSelectColor} onNavigate={noop} registerCell={noop} size="lg"
        />
      )}
    </div>
  );
}

// Vue mobile / tablette : une carte élève à la fois + navigation précédent / suivant + accès rapide.
export function PassportCards({ rows, columns, isCustom, evalSystem, onSelectColor, onSelectLevel, onSaveText }) {
  const [index, setIndex] = useState(0);
  useEffect(() => { setIndex((i) => Math.min(i, Math.max(rows.length - 1, 0))); }, [rows.length]);
  const r = rows[index];
  if (!r) return null;

  const go = (i) => {
    setIndex(Math.max(0, Math.min(rows.length - 1, i)));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex flex-col gap-3 pb-28" data-testid="passport-cards">
      <article
        key={r.passport_id}
        className="rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]"
        data-testid={`passport-card-${r.student_id}`}
      >
        <header className="mb-4 flex items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs tabular-nums text-slate-500 dark:bg-white/10">{String(index + 1).padStart(2, "0")}</span>
            <span className="truncate" data-testid="card-student-name">{r.student_name}</span>
          </h2>
          {r.signed ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" data-testid={`signature-${r.student_id}`} data-signed="true">
              <Check size={13} /> Signé
            </span>
          ) : (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid={`signature-${r.student_id}`} data-signed="false">
              <Clock size={13} /> En attente
            </span>
          )}
        </header>

        <div className="flex flex-col gap-2.5">
          {columns.map((c, colIndex) => (
            <CriterionField
              key={c.key} r={r} c={c} isCustom={isCustom} evalSystem={evalSystem}
              onSelectColor={onSelectColor} onSelectLevel={onSelectLevel} rowIndex={index} colIndex={colIndex}
            />
          ))}
        </div>

        <div className="mt-4 flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100">Notions à revoir</span>
            <InlineEditableCell
              passportId={r.passport_id} field="notions_a_revoir" value={r.notions_a_revoir}
              placeholder="Ajouter les notions à revoir…" onSave={onSaveText} size="lg" label="Notions à revoir"
              testid={`notions-${r.student_id}`}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100">Commentaire</span>
            <InlineEditableCell
              passportId={r.passport_id} field="commentaire" value={r.commentaire}
              placeholder="Ajouter un commentaire…" onSave={onSaveText} size="lg" label="Commentaire"
              testid={`commentaire-${r.student_id}`}
            />
          </label>
        </div>
      </article>

      {/* Navigation élève précédent / suivant (barre fixe en bas) */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-100 bg-white/90 px-3 py-2.5 backdrop-blur-xl dark:border-white/10 dark:bg-[#0A0A0A]/90 print:hidden"
        style={{ paddingBottom: "max(0.625rem, env(safe-area-inset-bottom))" }}
        aria-label="Navigation entre les élèves"
        data-testid="student-nav"
      >
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-2">
          <button
            type="button" onClick={() => go(index - 1)} disabled={index === 0}
            className="flex h-11 flex-1 items-center justify-center gap-1 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
            data-testid="prev-student"
          >
            <ChevronLeft size={18} /> Précédent
          </button>
          <StudentPicker rows={rows} index={index} onPick={go} />
          <button
            type="button" onClick={() => go(index + 1)} disabled={index === rows.length - 1}
            className="flex h-11 flex-1 items-center justify-center gap-1 rounded-xl bg-slate-900 text-sm font-semibold text-white disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 dark:bg-white dark:text-slate-900"
            data-testid="next-student"
          >
            Suivant <ChevronRight size={18} />
          </button>
        </div>
      </nav>
    </div>
  );
}
